import { useContext, useEffect, useMemo, useRef, useState } from "react";
import apiService, { formatRupees, handleError } from "../../../Common/ApiCommon/ApiService";
import Table from "../../../Common/Table";
import Pagination from "../../../Common/Pagination";
import icons from "../../../../assets/images/constantIcons";
import { UserContext } from "../../../Layout/UserContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const PaymentsRecords = () => {
    const [PlayersData, setPlayersData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [loading, setLoading] = useState(false);
    const { userData } = useContext(UserContext);
    const userId = userData?.userId;
    const [selectedFilter, setSelectedFilter] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
    const dropdownRef = useRef(null);
    const paymentModeDropdownRef = useRef(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState();
    const [isPaymentModeDropdownOpen, setIsPaymentModeDropdownOpen] = useState();

    const formatDate = (date) => {
        if (!date) return null;
        return date.toString();
    };


    // Fetch customer data from the API
    const fetchPlayers = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();

            // Payment type param
            if (selectedPaymentMode) {
                params.append("payment_type", selectedPaymentMode.toLowerCase());
            }

            // Date filter param
            if (selectedFilter === "today" || selectedFilter === "weekly" || selectedFilter === "monthly") {
                params.append("date_filter", selectedFilter);
            } else if (selectedFilter === "custom" && fromDate && toDate) {
                 params.append("date_filter", "custom");
                params.append("start_date", formatDate(fromDate));
                params.append("end_date", formatDate(toDate));
            }

            const url = `/vendors/${userId}/all_payment_history?${params.toString()}`;
            const response = await apiService.get(url);
            setPlayersData(response.data);
        } catch (errors) {
            handleError(errors);
            setPlayersData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlayers();
    }, [selectedPaymentMode, selectedFilter, fromDate, toDate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                setIsCustomDateOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Define table columns

    const columns = useMemo(
        () => [
            {
                Header: "id",
                accessor: "id",
                Cell: ({ value }) => (
                    <span className="" > {value} </span>),
            },
            {
                Header: "Date",
                accessor: "end_date",
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: "Player",
                accessor: "customer",
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: "Mode",
                accessor: "description",
                Cell: ({ value }) => <span>{value}</span>,
            },
            {
                Header: "Time",
                accessor: "transaction_time",
                width: "200px",
                Cell: ({ value }) => (
                    <div>
                        <p className="text-nowrap">{value}</p>
                    </div>
                ),
            },
            {
                Header: "Paid",
                accessor: "paid",
                Cell: ({ value }) => <span className="text-nowrap">{value}</span>,
            },
            {
                Header: "status",
                accessor: "status",
                Cell: ({ value }) => <span className={`px-3 text-center py-1 rounded-2xl text-white ${value === "Pending" ? "bg-yellow" : "bg-green"}`}>{value}</span>,
            },
        ],
        []
    );

    const data = useMemo(() => {
        if (!Array.isArray(PlayersData)) return [];
        return PlayersData
            .filter((payment) => payment?.status === "paid")
            .filter((payment) => selectedPaymentMode ? payment.payment_type?.toLowerCase() === selectedPaymentMode.toLowerCase() : true)
            .map((payment) => ({
                id: payment.id,
                payment_id: payment.id,
                end_date: new Date(payment.end_date || new Date()).toLocaleDateString(
                    "en-GB"
                ),
                transaction_time: payment.transaction_time,
                description: payment.payment_type || "-",
                paid: formatRupees(payment.paid),
                status: payment.status === "pending" ? "Pending" : "Paid",
                customer: payment.customer,
            }));
    }, [PlayersData]);

    // Paginate the data
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

    const filters = ["Today", "Weekly", "Monthly"];

    const handleFilterChange = (filter) => {
        if (filter === "Custom Date") {
            setIsCustomDateOpen((prev) => !prev);
        } else {
            setSelectedFilter(filter.toLowerCase());
            setIsDropdownOpen(false);
        }
    };

    const applyFilter = () => {
        if (fromDate && toDate) {
            setSelectedFilter("custom");
        }
        setIsDropdownOpen(false);
        setIsCustomDateOpen(false);
    };


    const downloadTransactions = async () => {
        if (data.length === 0) {
            toast.warn("No transactions available to download.", { duration: 2000 });
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Payment History");

        // Define your headers manually since some column headers are functions or missing
        const headers = [
            { header: "ID", key: "id", width: 10 },
            { header: "Date", key: "end_date", width: 15 },
            { header: "Player", key: "customer", width: 25 },
            { header: "Mode", key: "description", width: 15 },
            { header: "Time", key: "transaction_time", width: 15 },
            { header: "Paid", key: "paid", width: 15 },
            { header: "Status", key: "status", width: 15 },
        ];

        worksheet.columns = headers;

        // Add data rows
        data.forEach((row) => {
            const formattedRow = {
                id: row.id,
                end_date: row.end_date,
                customer: row.customer,
                description: row.description,
                transaction_time: row.transaction_time,
                paid: row.paid,
                status: row.status,
            };

            const addedRow = worksheet.addRow(formattedRow);
            addedRow.eachCell((cell) => {
                cell.alignment = { horizontal: "center", vertical: "middle" };
            });
        });

        // Style header row
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        worksheet.eachRow({ includeEmpty: false }, (row) => {
            row.height = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const now = new Date();
        const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
        const time = now
            .toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
            .replace(/:/g, "-")
            .replace(/\s/g, "");

        const fileName = `PaymentHistory_${date}_${time}.xlsx`;
        saveAs(blob, fileName);
    };


    return (
        <div >
            <div className=" flex flex-col p-4">
                <div className="flex flex-col">
                    <div className="flex justify-between">
                        <div>
                            <h1 className="main_heading text-lg sm:text-[22px] flex gap-3 items-center mb-3">
                                <p>Payment Records</p>
                            </h1>
                        </div>
                        <div className="flex sx:flex-wrap sm:flex-nowrap gap-1 md:gap-3">
                            <div
                                onClick={downloadTransactions}
                                className="bg-white text-green rounded-md shadow-sm text-base sm:text-2xl xs:h-10 md:h-auto p-2 cursor-pointer"
                            >
                                <icons.FiDownload />
                            </div>
                            {/* paymen tmode */}
                            <div className="relative" ref={paymentModeDropdownRef}>
                                <div
                                    className="flex border items-center text-G100 p-2 rounded-lg font-normal bg-white cursor-pointer"
                                    onClick={() => setIsPaymentModeDropdownOpen(!isPaymentModeDropdownOpen)}
                                >
                                    <span className="w-28 md:w-[155px] text-sm ">
                                        {selectedPaymentMode ? selectedPaymentMode : "Payment Mode"}
                                    </span>
                                    <icons.RiArrowDropDownLine className="ml-2 text-2xl" />
                                </div>

                                {isPaymentModeDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-22 md:w-52 bg-white border rounded-lg shadow-lg z-10 p-2">
                                        {["Cash", "Card", "UPI"].map((mode) => (
                                            <div
                                                key={mode}
                                                className={`text-sm p-2 rounded-md cursor-pointer hover:bg-green50 ${selectedPaymentMode === mode ? "text-green font-semibold" : "text-G500"
                                                    }`}
                                                onClick={() => {
                                                    setSelectedPaymentMode(mode);
                                                    setIsPaymentModeDropdownOpen(false);

                                                }}
                                            >
                                                {mode}
                                            </div>
                                        ))}

                                        <div
                                            onClick={() => {
                                                setSelectedPaymentMode("");
                                                setIsPaymentModeDropdownOpen(false);
                                            }}
                                            className="w-full text-sm p-2 rounded-md cursor-pointer hover:bg-green50 text-G500"
                                        >
                                            Clear Payment Mode
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative" ref={dropdownRef}>
                                <div
                                    className="flex border items-center text-G100 p-2 rounded-lg font-normal bg-white cursor-pointer"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <icons.GoSearch className="mr-2" />
                                    <span className="w-36 md:w-[160px] text-sm">
                                        {selectedFilter
                                            ? selectedFilter.charAt(0).toUpperCase() +
                                            selectedFilter.slice(1).toLowerCase()
                                            : "Date period search"}
                                    </span>
                                    <icons.RiArrowDropDownLine className="ml-2 text-2xl" />
                                </div>

                                {/* Dropdown Menu */}
                                {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-52 md:w-56 bg-white border rounded-lg shadow-lg z-10 p-2">
                                        {filters.map((filter) => (
                                            <div
                                                key={filter}
                                                className={`text-sm p-2 rounded-md cursor-pointer hover:bg-green50 ${selectedFilter === filter.toLowerCase()
                                                    ? "text-green font-semibold"
                                                    : "text-G500 "
                                                    }`}
                                                onClick={() => handleFilterChange(filter)}
                                            >
                                                {filter}
                                            </div>
                                        ))}

                                        {/* Custom Date Picker */}
                                        <div
                                            className="flex justify-between items-center px-2 py-2 hover:bg-green50 rounded-md cursor-pointer"
                                            onClick={() => handleFilterChange("Custom Date")}
                                        >
                                            <span className=" text-sm text-G500 ">Custom Date</span>
                                            <icons.MdOutlineCalendarMonth className="text-green700 text-xl" />
                                        </div>
                                        {isCustomDateOpen && (
                                            <div className="p-2">
                                                <DatePicker
                                                    selected={fromDate}
                                                    onChange={setFromDate}
                                                    selectsStart
                                                    startDate={fromDate}
                                                    endDate={toDate}
                                                    placeholderText="From Date"
                                                    className="border px-3 py-1 rounded w-full mb-2 outline-none text-sm"
                                                    dateFormat="yyyy-MM-dd"
                                                />
                                                <DatePicker
                                                    selected={toDate}
                                                    onChange={setToDate}
                                                    selectsEnd
                                                    startDate={fromDate}
                                                    endDate={toDate}
                                                    minDate={fromDate}
                                                    placeholderText="To Date"
                                                    className="border px-3 py-1 rounded w-full outline-none text-sm"
                                                    dateFormat="yyyy-MM-dd"
                                                />

                                                {/* Buttons */}
                                                <div className="flex justify-between mt-2">
                                                    <button
                                                        className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                                                        onClick={() => {
                                                            setIsDropdownOpen(false);
                                                            setIsCustomDateOpen(false);
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={applyFilter}
                                                        className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            onClick={() => {
                                                setSelectedFilter("");
                                                setFromDate(null);
                                                setToDate(null);
                                                setIsDropdownOpen(false);
                                                setIsCustomDateOpen(false);
                                            }}
                                            className="w-full text-sm p-2 rounded-md cursor-pointer hover:bg-green50 text-G500 "
                                        >
                                            Clear filters
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <div className="flex justify-center items-center my-4">
                        <p className="text-green700 font-medium">Loading data...</p>
                    </div>
                ) : (
                    <div className="my-2 mb-6 overflow-auto vertical max-h-[70vh] horizontalscroll">
                        <Table columns={columns} data={currentRows} />
                    </div>
                )}

                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={data.length}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(newRows) => {
                        setRowsPerPage(newRows);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
};

export default PaymentsRecords;
